import {
  EditOutlined,
  EyeOutlined,
  FilterOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import {
  Button,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import React, { useCallback, useRef, useState } from 'react';
import settings from '../../settings';

import { HolisticProjectTypes } from '@aims/shared/holistic-projects/constants';
import ReportInfoItem from '@aims/shared/reports/common/ReportInfoItem';
import { getQuarterLabel } from '@aims/shared/reports/quarters';
import { displayMoney, getNumberValue } from '@aims/shared/shared/utils';

import useManyRemoteM1 from '@aims/shared/shared/use-many-remote-m1';
import { gql } from '@apollo/client';
import { shallowEqual, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import SelectPeopleGroup from '@aims/shared/people-groups/SelectPeopleGroup';
import { extractAllHolisticQuarterlyReportsForAdmin } from './constants';
import { ReportApprovalStatuses } from '@aims/shared/reports/constants';
import SelectRegionForAdmin from '../people_groups/ListPeopleGroups/SelectRegionForAdmin';
import SelectContact from '../contacts/SelectContact';
import { ScheduleFrequencies } from '@aims/shared/report-schedules/constants';

const allHolisticQuarterlyReportsForAdminQuery = gql`
  query AllHolisticQuarterlyReportsForAdmin(
    $first: Int
    $after: String
    $filters: HolisticQuarterlyReportFiltersForAdmin
    $sortBy: [SortBy]
  ) {
    allHolisticQuarterlyReportsForAdmin(
      first: $first
      after: $after
      filters: $filters
      sortBy: $sortBy
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          _id
          contactId
          contact {
            _id
            fullName
          }
          quarter
          regionCode
          reportType
          isSubmitted
          isApproved
          submittedAt
          createdBy
          createdByUser {
            _id
            name
          }
          submittedByUser {
            _id
            name
          }
          updatedBy
          createdAt
          updatedAt
          updatedByUser {
            _id
            name
          }
          status
          approval {
            status
            when
            approver {
              _id
              name
            }
          }
          holisticProjectId
          holisticProject {
            _id
            projectName
            projectNo
            typeOfProject
          }
          projectCoordId
          projectCoord {
            _id
            fullName
          }
          currency
          exchangeRate
          projectState
          hasReceivedFunds
          loadPaymentsThisQuarterUsd
          loanPaymentsTotalUsd
          loanTotal
          loanTerm
          expensesThisQuarterForeign
          revenueThisQuarterForeign
          supportedFieldWorkers {
            amountForeign
            contactId
            name
          }
          miracles {
            _id
            desc
          }
          prayerRequests {
            _id
            desc
          }
        }
      }
    }
  }
`;

const { Text } = Typography;

const keyMap = {
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
  submittedAt: 'submittedAt',
  typeOfProject: 'typeOfProject.keyword',
  projectName: 'projectName.keyword',
  loanTotalUsd: 'loanTotalUsd',
};

const queryId = 'holisticQuarterlyReportTabs';

function HolisticProjectReportsTypeTab({
  reportStatus,
  activeReportStatus,
  holisticProjectId,
  sharedFilters,
  onSharedFiltersChange,
  reportCategoryKey,
}) {
  const [form] = Form.useForm();
  const [sortedBy, setSortedBy] = useState('updatedAt');
  const [sortOrder, setSortOrder] = useState('DESC');
  const location = useLocation();

  const [showFilters, setShowFilters] = useState(false);
  const sortBy = useRef([
    { key: sortedBy, order: sortOrder },
    { key: '_score', order: 'DESC' },
  ]);

  const filters = useRef({
    status: reportStatus,
    holisticProjectId,
    ...(sharedFilters?.peopleGroupId
      ? { peopleGroupId: sharedFilters.peopleGroupId }
      : {}),
    ...(sharedFilters?.regionCode
      ? { regionCode: sharedFilters.regionCode }
      : {}),
    ...(sharedFilters?.projectCoordId
      ? { projectCoordId: sharedFilters.projectCoordId }
      : {}),
  });

  const {
    loading,
    data: reports,
    search: reportSearch,
    hasNextPage,
    next,
    refetch,
  } = useManyRemoteM1({
    query: allHolisticQuarterlyReportsForAdminQuery,
    extract: extractAllHolisticQuarterlyReportsForAdmin,
    first: settings.querySize,
    filters: filters.current,
    sortBy: sortBy.current,
    fetchPolicy: 'network-only',
    queryId,
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: settings.pageSize,
  });
  const handleTableChange = useCallback((pages) => {
    setPagination({
      ...pages,
    });
  }, []);
  const locale = useSelector((store) => store.locale, shallowEqual);

  const getFinances = (report) => {
    const exchangeRate = getNumberValue(report && report.exchangeRate);
    if (exchangeRate > 0) {
      const supportToFieldWorkers = (report.supportedFieldWorkers || []).reduce(
        (prev, curr) => {
          return prev + getNumberValue(curr && curr.amountForeign);
        },
        0,
      );
      return {
        expenses:
          getNumberValue(report.expensesThisQuarterForeign) / exchangeRate,
        revenue:
          getNumberValue(report.revenueThisQuarterForeign) / exchangeRate,
        loanPayments: getNumberValue(report.loadPaymentsThisQuarterUsd),
        toFieldWorkers: supportToFieldWorkers / exchangeRate,
        received: report.hasReceivedFunds,
      };
    }
    return {};
  };

  const handleSortByChange = useCallback(
    (value) => {
      sortBy.current = [
        { key: keyMap[value], order: sortOrder },
        { key: '_score', order: 'DESC' },
      ];
      setSortedBy(value);
      refetch();
    },
    [sortOrder, refetch],
  );
  const handleSortOrderChange = useCallback(
    (value) => {
      sortBy.current = [
        { key: keyMap[sortedBy], order: value },
        { key: '_score', order: 'DESC' },
      ];
      setSortOrder(value);
      refetch();
    },
    [sortedBy, refetch],
  );
  const handleSearch = useCallback(
    (event) => {
      sortBy.current = [
        { key: '_score', order: 'DESC' },
        { key: keyMap[sortedBy], order: sortOrder },
      ];
      reportSearch(event.currentTarget.value);
    },
    [reportSearch, sortOrder, sortedBy],
  );
  const handlePeopleGroupChange = useCallback(
    (value) => {
      const nextSharedFilters = {
        ...sharedFilters,
        peopleGroupId: value || undefined,
      };
      onSharedFiltersChange(nextSharedFilters);

      const nextFilters = {
        status: reportStatus,
        holisticProjectId,
      };
      if (nextSharedFilters.peopleGroupId) {
        nextFilters.peopleGroupId = nextSharedFilters.peopleGroupId;
      }
      if (nextSharedFilters.regionCode) {
        nextFilters.regionCode = nextSharedFilters.regionCode;
      }
      if (nextSharedFilters.projectCoordId) {
        nextFilters.projectCoordId = nextSharedFilters.projectCoordId;
      }
      filters.current = nextFilters;
      refetch();
    },
    [
      holisticProjectId,
      onSharedFiltersChange,
      refetch,
      reportStatus,
      sharedFilters,
    ],
  );

  const applySharedFilters = useCallback(
    (nextSharedFilters, shouldRefetch = false) => {
      onSharedFiltersChange(nextSharedFilters);

      const nextFilters = {
        status: reportStatus,
        holisticProjectId,
      };

      if (nextSharedFilters.peopleGroupId) {
        nextFilters.peopleGroupId = nextSharedFilters.peopleGroupId;
      }
      if (nextSharedFilters.regionCode) {
        nextFilters.regionCode = nextSharedFilters.regionCode;
      }
      if (nextSharedFilters.projectCoordId) {
        nextFilters.projectCoordId = nextSharedFilters.projectCoordId;
      }
      filters.current = nextFilters;
      if (shouldRefetch) {
        refetch();
      }
    },
    [holisticProjectId, onSharedFiltersChange, refetch, reportStatus],
  );

  const handleRegionChange = useCallback(
    (value) => {
      applySharedFilters(
        {
          ...sharedFilters,
          regionCode: value || undefined,
        },
        true,
      );
    },
    [applySharedFilters, sharedFilters],
  );

  const handleCoordinatorChange = useCallback(
    (value) => {
      applySharedFilters(
        {
          ...sharedFilters,
          projectCoordId: value || undefined,
        },
        true,
      );
    },
    [applySharedFilters, sharedFilters],
  );

  const handleProjectNumberChange = useCallback(
    (event) => {
      applySharedFilters({
        ...sharedFilters,
        projectNumberSearch: event?.target?.value || '',
      });
    },
    [applySharedFilters, sharedFilters],
  );

  const handleFrequencyChange = useCallback(
    (value) => {
      applySharedFilters({
        ...sharedFilters,
        frequency: value || 'ALL',
      });
    },
    [applySharedFilters, sharedFilters],
  );

  const filteredReports = React.useMemo(() => {
    const projectNumberSearch = (sharedFilters?.projectNumberSearch || '')
      .trim()
      .toUpperCase();
    const frequency = sharedFilters?.frequency || 'ALL';

    return (reports || []).filter((record) => {
      if (projectNumberSearch) {
        const projectNumber = `${
          record?.holisticProject?.projectNumber ||
          record?.holisticProject?.projectNo ||
          ''
        }`.toUpperCase();
        if (!projectNumber.includes(projectNumberSearch)) {
          return false;
        }
      }

      if (frequency && frequency !== 'ALL') {
        const rowFrequency = (record?.frequency || 'QUARTERLY').toUpperCase();
        if (rowFrequency !== frequency) {
          return false;
        }
      }

      return true;
    });
  }, [reports, sharedFilters]);

  React.useEffect(() => {
    form.setFieldsValue({
      regionCode: sharedFilters?.regionCode,
      projectCoordId: sharedFilters?.projectCoordId,
      projectNumberSearch: sharedFilters?.projectNumberSearch || '',
      frequency: sharedFilters?.frequency || 'ALL',
      peopleGroupId: sharedFilters?.peopleGroupId,
    });
  }, [form, sharedFilters]);

  React.useEffect(() => {
    const nextFilters = {
      status: reportStatus,
      holisticProjectId,
    };
    if (sharedFilters?.peopleGroupId) {
      nextFilters.peopleGroupId = sharedFilters.peopleGroupId;
    }
    if (sharedFilters?.regionCode) {
      nextFilters.regionCode = sharedFilters.regionCode;
    }
    if (sharedFilters?.projectCoordId) {
      nextFilters.projectCoordId = sharedFilters.projectCoordId;
    }
    filters.current = nextFilters;
  }, [holisticProjectId, reportStatus, sharedFilters]);

  React.useEffect(() => {
    if (activeReportStatus === reportStatus) {
      refetch();
    }
  }, [
    activeReportStatus,
    refetch,
    reportStatus,
    sharedFilters?.peopleGroupId,
    sharedFilters?.regionCode,
    sharedFilters?.projectCoordId,
  ]);

  const handleLoadMore = useCallback(() => {
    next();
  }, [next]);
  return (
    <>
      <div>
        <Form layout="vertical" form={form}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              marginBottom: 16,
            }}
          >
            {showFilters && (
              <Button
                icon={<FilterOutlined />}
                onClick={() => {
                  setShowFilters(false);
                }}
              >
                Hide Filters
              </Button>
            )}
            {!showFilters && (
              <Button
                icon={<FilterOutlined />}
                onClick={() => setShowFilters(true)}
              >
                Show Filters
              </Button>
            )}
            <Button icon={<RedoOutlined />} onClick={refetch}>
              Refresh
            </Button>
          </div>
          {showFilters && (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                <Form.Item
                  label="Search"
                  style={{ flex: 3, marginRight: 16, minWidth: 200 }}
                >
                  <Input onChange={handleSearch} />
                </Form.Item>
                <Form.Item
                  label="People Group"
                  name="peopleGroupId"
                  style={{ flex: 1, minWidth: 200, marginRight: 16 }}
                >
                  <SelectPeopleGroup
                    disabled={loading}
                    onChange={handlePeopleGroupChange}
                  />
                </Form.Item>
                <Form.Item
                  label="Region"
                  name="regionCode"
                  style={{ flex: 1, minWidth: 220, marginRight: 16 }}
                >
                  <SelectRegionForAdmin
                    disabled={loading}
                    allowClear
                    onChange={handleRegionChange}
                  />
                </Form.Item>
                <Form.Item
                  label="Coordinator"
                  name="projectCoordId"
                  style={{ flex: 1, minWidth: 220, marginRight: 16 }}
                >
                  <SelectContact
                    disabled={loading}
                    allowClear
                    filters={{ isRegionalCoord: true }}
                    onChange={handleCoordinatorChange}
                  />
                </Form.Item>
                <Form.Item
                  label="Sort By"
                  style={{ flex: 2, marginRight: 16, minWidth: 200 }}
                >
                  <Select
                    placeholder="Select ..."
                    value={sortedBy}
                    onChange={handleSortByChange}
                    disabled={loading}
                  >
                    <Select.Option value="updatedAt">
                      Last Updated
                    </Select.Option>
                    <Select.Option value="createdAt">
                      Created Date
                    </Select.Option>
                    <Select.Option value="submittedAt">
                      Submitted Date
                    </Select.Option>
                    <Select.Option value="typeOfProject">
                      Type of Project
                    </Select.Option>
                    <Select.Option value="projectName">
                      Project Name
                    </Select.Option>
                    <Select.Option value="loanTotalUsd">
                      Loan Total
                    </Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Sort Order"
                  style={{ flex: 1, minWidth: 100 }}
                >
                  <Select
                    placeholder="Select ..."
                    value={sortOrder}
                    onChange={handleSortOrderChange}
                    disabled={loading}
                  >
                    <Select.Option value="ASC">Ascending</Select.Option>
                    <Select.Option value="DESC">Descending</Select.Option>
                  </Select>
                </Form.Item>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                <Form.Item
                  label="Project #"
                  name="projectNumberSearch"
                  style={{ flex: 1, minWidth: 200, marginRight: 16 }}
                >
                  <Input
                    disabled={loading}
                    placeholder="e.g. NA-HP-0049"
                    onChange={handleProjectNumberChange}
                  />
                </Form.Item>
                <Form.Item
                  label="Frequency"
                  name="frequency"
                  style={{ flex: 1, minWidth: 200, marginRight: 16 }}
                >
                  <Select disabled={loading} onChange={handleFrequencyChange}>
                    <Select.Option value="ALL">All</Select.Option>
                    <Select.Option value={ScheduleFrequencies.WEEKLY.key}>
                      {ScheduleFrequencies.WEEKLY.label}
                    </Select.Option>
                    <Select.Option value={ScheduleFrequencies.MONTHLY.key}>
                      {ScheduleFrequencies.MONTHLY.label}
                    </Select.Option>
                    <Select.Option value={ScheduleFrequencies.QUARTERLY.key}>
                      {ScheduleFrequencies.QUARTERLY.label}
                    </Select.Option>
                    <Select.Option value={ScheduleFrequencies.CUSTOM.key}>
                      {ScheduleFrequencies.CUSTOM.label}
                    </Select.Option>
                  </Select>
                </Form.Item>
              </div>
            </>
          )}
        </Form>
      </div>
      <Table
        dataSource={filteredReports}
        loading={!filteredReports.length && loading}
        pagination={{ ...pagination, total: filteredReports.length }}
        onChange={handleTableChange}
        // style={{ width: '100%' }}
        rowKey="_id"
        scroll={{ x: 1200 }}
      >
        <Table.Column
          title="Label"
          key="quarter"
          render={(text, record) => getQuarterLabel(record.quarter)}
        />
        <Table.Column
          title="Project Name"
          key="projectName"
          render={(text, record) => {
            return record.holisticProject && record.holisticProject.projectName;
          }}
        />
        <Table.Column
          title="Type of Project"
          key="typeOfProject"
          render={(text, record) => {
            const type =
              record.holisticProject &&
              record.holisticProject.typeOfProject &&
              HolisticProjectTypes[record.holisticProject.typeOfProject];
            return type && type.label;
          }}
        />
        <Table.Column
          title="Status"
          key="status"
          render={(text, record) => {
            const status = ReportApprovalStatuses[record.status];
            return (
              status && (
                <Text
                  style={{
                    color: status.color,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {status.label}
                </Text>
              )
            );
          }}
        />
        <Table.Column
          title="Last Update"
          key="lastUpdate"
          render={(text, record) => {
            if (
              record.status === ReportApprovalStatuses.APPROVED.key &&
              record.approval
            ) {
              return (
                <>
                  {`Approved on ${new Intl.DateTimeFormat(locale, {
                    dateStyle: 'medium',
                  }).format(new Date(record.approval.when))}`}
                  {record.approval.approver && (
                    <>
                      <br />
                      {`by ${record.approval.approver.name}`}
                    </>
                  )}
                </>
              );
            }
            if (record.status === ReportApprovalStatuses.DRAFT.key) {
              return (
                <>
                  {`Updated on ${new Intl.DateTimeFormat(locale, {
                    dateStyle: 'medium',
                  }).format(new Date(record.updatedAt))}`}
                  {record.updatedByUser && (
                    <>
                      <br />
                      {`by ${record.updatedByUser.name}`}
                    </>
                  )}
                </>
              );
            }
            if (
              record.status === ReportApprovalStatuses.REJECTED.key &&
              record.approval
            ) {
              return (
                <>
                  {`Rejected on ${new Intl.DateTimeFormat(locale, {
                    dateStyle: 'medium',
                  }).format(new Date(record.approval.when))}`}
                  {record.approval.approver && (
                    <>
                      <br />
                      {`by ${record.approval.approver.name}`}
                    </>
                  )}
                </>
              );
            }
            if (record.status === ReportApprovalStatuses.PENDING.key) {
              return (
                <>
                  {`Submitted on ${new Intl.DateTimeFormat(locale, {
                    dateStyle: 'medium',
                  }).format(new Date(record.submittedAt))}`}
                  {record.submittedByUser && (
                    <>
                      <br />
                      {`by ${record.submittedByUser.name}`}
                    </>
                  )}
                </>
              );
            }
            if (
              record.status === ReportApprovalStatuses.HOLD.key &&
              record.approval
            ) {
              return (
                <>
                  {`Put on hold ${new Intl.DateTimeFormat(locale, {
                    dateStyle: 'medium',
                  }).format(new Date(record.approval.when))}`}
                  {record.createdByUser && (
                    <>
                      <br />
                      {`by ${record.createdByUser.name}`}
                    </>
                  )}
                </>
              );
            }
            return null;
          }}
        />
        <Table.Column
          title="Loan Funds Received"
          key="loanFundsReceived"
          render={(text, record) => {
            const finances = getFinances(record);
            return finances.received ? 'Yes' : 'No';
          }}
        />
        <Table.Column
          title="Financial Update"
          key="financialUpdate"
          width={300}
          render={(text, record) => {
            const finances = getFinances(record);
            return (
              <div>
                <ReportInfoItem
                  label="Loan funds received"
                  value={finances.received ? 'Yes' : 'No'}
                />
                <ReportInfoItem
                  label="Loan payments made"
                  value={displayMoney(finances.loanPayments)}
                />
                <ReportInfoItem
                  label="Quarterly Revenue"
                  value={displayMoney(finances.revenue)}
                />
                <ReportInfoItem
                  label="Quarterly Expenses"
                  value={displayMoney(finances.expenses)}
                />
                <ReportInfoItem
                  label="Support to Field Workers"
                  value={displayMoney(finances.toFieldWorkers)}
                />
              </div>
            );
          }}
        />
        <Table.Column
          title="Action"
          key="action"
          render={(text, record) => {
            const backLinkParams = new URLSearchParams({
              from: '/holistic-project-reports',
              tab: 'legacy',
              ...(reportCategoryKey && { reportCategoryKey }),
            }).toString();
            return (
              <Space size="middle">
                <Tooltip title="View">
                  <Link
                    to={`/holistic-project-reports/review/${record._id}?${backLinkParams}`}
                  >
                    <Button icon={<EyeOutlined />} />
                  </Link>
                </Tooltip>
                <Tooltip title="Edit">
                  <Link
                    to={`/holistic-project-reports/edit/${record._id}?${backLinkParams}`}
                  >
                    <Button icon={<EditOutlined />} />
                  </Link>
                </Tooltip>
              </Space>
            );
          }}
        />
      </Table>
      <div className="bottom-actions">
        {hasNextPage && <Button onClick={handleLoadMore}>Load More</Button>}
      </div>
      <style jsx>{`
        .bottom-actions {
          text-align: center;
        }
        .info {
          display: flex;
          flex-wrap: wrap;
          margin-left: -16px;
          margin-right: -16px;
          max-width: 600px;
        }
      `}</style>
    </>
  );
}

export default HolisticProjectReportsTypeTab;
